import { useId, type InputHTMLAttributes, type ReactNode } from "react";

import { cn } from "@/shared/lib/cn";

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Message d'erreur **déjà traduit** : ce composant n'appelle pas `t()`. */
  error?: string | undefined;
  hint?: ReactNode;
}

/**
 * Champ de formulaire accessible.
 *
 * `aria-describedby` relie l'erreur et l'aide à l'entrée : sans lui, un lecteur
 * d'écran annonce « champ invalide » sans jamais dire pourquoi (docs/24 §3).
 */
export function Field({ label, error, hint, className, id, ...props }: FieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");

  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="block text-sm font-medium">
        {label}
      </label>
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy === "" ? undefined : describedBy}
        className={cn(
          "block w-full rounded-md border px-3 py-2 text-sm",
          "border-slate-300 bg-white text-slate-900",
          "focus:border-brand-500 focus:ring-brand-500 focus:ring-1 focus:outline-none",
          "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100",
          error && "border-red-500 dark:border-red-500",
          className,
        )}
        {...props}
      />
      {hint !== undefined && (
        <p id={hintId} className="text-xs text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} role="alert" className="text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
